'use client';

import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

interface VpsAppHeroVisualProps {
  heroVisualType: 'workflow' | 'terminal' | 'chat' | 'dashboard';
  accentColor: 'orange' | 'blue' | 'green' | 'purple' | 'cyan';
  appName: string;
  className?: string;
}

const accentColors = {
  orange: {
    primary: 'text-orange-400',
    bg: 'bg-orange-500/20',
    border: 'border-orange-500/30',
    glow: 'rgba(249,115,22,0.4)',
    glowLight: 'rgba(249,115,22,0.15)',
    ring: 'ring-orange-500/30',
    gradient: 'from-orange-400 to-orange-600',
    line: 'bg-orange-400',
    badge: 'bg-orange-500/20 text-orange-400',
  },
  blue: {
    primary: 'text-blue-400',
    bg: 'bg-blue-500/20',
    border: 'border-blue-500/30',
    glow: 'rgba(59,130,246,0.4)',
    glowLight: 'rgba(59,130,246,0.15)',
    ring: 'ring-blue-500/30',
    gradient: 'from-blue-400 to-blue-600',
    line: 'bg-blue-400',
    badge: 'bg-blue-500/20 text-blue-400',
  },
  green: {
    primary: 'text-emerald-400',
    bg: 'bg-emerald-500/20',
    border: 'border-emerald-500/30',
    glow: 'rgba(16,185,129,0.4)',
    glowLight: 'rgba(16,185,129,0.15)',
    ring: 'ring-emerald-500/30',
    gradient: 'from-emerald-400 to-emerald-600',
    line: 'bg-emerald-400',
    badge: 'bg-emerald-500/20 text-emerald-400',
  },
  purple: {
    primary: 'text-violet-400',
    bg: 'bg-violet-500/20',
    border: 'border-violet-500/30',
    glow: 'rgba(139,92,246,0.4)',
    glowLight: 'rgba(139,92,246,0.15)',
    ring: 'ring-violet-500/30',
    gradient: 'from-violet-400 to-violet-600',
    line: 'bg-violet-400',
    badge: 'bg-violet-500/20 text-violet-400',
  },
  cyan: {
    primary: 'text-cyan-400',
    bg: 'bg-cyan-500/20',
    border: 'border-cyan-500/30',
    glow: 'rgba(6,182,212,0.4)',
    glowLight: 'rgba(6,182,212,0.15)',
    ring: 'ring-cyan-500/30',
    gradient: 'from-cyan-400 to-cyan-600',
    line: 'bg-cyan-400',
    badge: 'bg-cyan-500/20 text-cyan-400',
  },
};

/**
 * VPS App Hero Visual — shows a themed mockup per heroVisualType.
 * - workflow: n8n-style workflow builder with connected nodes
 * - terminal: terminal with openclaw onboard output
 */
export function VpsAppHeroVisual({
  heroVisualType,
  accentColor,
  appName,
  className,
}: VpsAppHeroVisualProps) {
  const colors = accentColors[accentColor];

  if (heroVisualType === 'workflow') {
    return <WorkflowVisual colors={colors} className={className} />;
  }

  if (heroVisualType === 'terminal') {
    return <TerminalVisual colors={colors} appName={appName} className={className} />;
  }

  // Fallback for chat/dashboard
  return <TerminalVisual colors={colors} appName={appName} className={className} />;
}

// ─── Workflow Visual (n8n) ───────────────────────────────────────────────────

function WorkflowVisual({
  colors,
  className,
}: { colors: (typeof accentColors)['orange']; className?: string }) {
  const t = useTranslations('vpsApps.n8n.features.items');

  return (
    <div className={cn('relative w-full aspect-[4/3] max-w-2xl mx-auto', className)}>
      {/* Ambient glow */}
      <div
        className="absolute z-0 right-[10%] bottom-[20%] w-[45%] h-[50%] rounded-full blur-2xl"
        style={{
          background: `radial-gradient(circle, ${colors.glow} 0%, ${colors.glowLight} 45%, transparent 70%)`,
        }}
      />

      {/* Monitor frame */}
      <div
        className="absolute left-[5%] md:left-[8%] bottom-[12%] w-[72%] md:w-[68%] z-10"
        style={{
          transform: 'perspective(1200px) rotateY(6deg) rotateX(1deg)',
          transformOrigin: 'left center',
        }}
      >
        <div
          className="relative rounded-xl md:rounded-xl overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, #1f2937 0%, #111827 100%)',
            boxShadow: '0 25px 50px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.05) inset',
            padding: '8px',
          }}
        >
          <div
            className="relative rounded-lg md:rounded-xl overflow-hidden"
            style={{ background: '#0f172a', padding: '4px' }}
          >
            <div className="relative rounded-md md:rounded-lg overflow-hidden aspect-video bg-[#1a1a2e]">
              {/* Header bar */}
              <div className="flex items-center gap-1.5 px-2 md:px-3 py-1.5 md:py-2 bg-gray-900 border-b border-gray-700">
                <div className="flex gap-1">
                  <div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-red-500" />
                  <div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-yellow-500" />
                  <div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-green-500" />
                </div>
                <div className="flex-1 ml-2 md:ml-3">
                  <span className="text-[10px] md:text-[11px] text-gray-400 font-mono">
                    n8n — Workflow Editor
                  </span>
                </div>
              </div>

              {/* Workflow canvas */}
              <div className="relative h-[calc(100%-28px)] md:h-[calc(100%-32px)] p-2 md:p-3">
                {/* Grid pattern */}
                <div
                  className="absolute inset-0 opacity-10"
                  style={{
                    backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
                    backgroundSize: '16px 16px',
                  }}
                />

                {/* SVG connections */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none z-0">
                  {/* Trigger → HTTP */}
                  <line
                    x1="27%"
                    y1="32%"
                    x2="47%"
                    y2="22%"
                    stroke="#f97316"
                    strokeWidth="2"
                    strokeDasharray="4 2"
                    className="animate-[dash_2s_linear_infinite]"
                    opacity="0.6"
                  />
                  {/* Trigger → Set */}
                  <line
                    x1="27%"
                    y1="38%"
                    x2="47%"
                    y2="58%"
                    stroke="#f97316"
                    strokeWidth="2"
                    strokeDasharray="4 2"
                    className="animate-[dash_2s_linear_infinite]"
                    opacity="0.6"
                  />
                  {/* HTTP → IF */}
                  <line
                    x1="62%"
                    y1="22%"
                    x2="78%"
                    y2="40%"
                    stroke="#f97316"
                    strokeWidth="2"
                    strokeDasharray="4 2"
                    className="animate-[dash_2s_linear_infinite]"
                    opacity="0.6"
                  />
                  {/* Set → IF */}
                  <line
                    x1="62%"
                    y1="58%"
                    x2="78%"
                    y2="48%"
                    stroke="#f97316"
                    strokeWidth="2"
                    strokeDasharray="4 2"
                    className="animate-[dash_2s_linear_infinite]"
                    opacity="0.6"
                  />
                </svg>

                {/* Workflow nodes */}
                <div className="relative z-10 w-full h-full">
                  {/* Trigger node */}
                  <div className="absolute left-[8%] top-[25%] flex flex-col items-center">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-orange-500/30 border border-orange-500/50 flex items-center justify-center shadow-lg shadow-orange-500/10">
                      <svg
                        className="w-4 h-4 md:w-5 md:h-5 text-orange-400"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                      </svg>
                    </div>
                    <span className="text-[9px] md:text-[10px] text-gray-400 mt-1 font-mono">
                      Webhook
                    </span>
                  </div>

                  {/* HTTP node */}
                  <div className="absolute left-[40%] top-[10%] flex flex-col items-center">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-blue-500/30 border border-blue-500/50 flex items-center justify-center shadow-lg shadow-blue-500/10">
                      <svg
                        className="w-4 h-4 md:w-5 md:h-5 text-blue-400"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
                        <path d="M2 12h20M12 2c2.5 3 4 6.5 4 10s-1.5 7-4 10c-2.5-3-4-6.5-4-10s1.5-7 4-10z" />
                      </svg>
                    </div>
                    <span className="text-[9px] md:text-[10px] text-gray-400 mt-1 font-mono">
                      HTTP
                    </span>
                  </div>

                  {/* Set/Transform node */}
                  <div className="absolute left-[40%] top-[48%] flex flex-col items-center">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-emerald-500/30 border border-emerald-500/50 flex items-center justify-center shadow-lg shadow-emerald-500/10">
                      <svg
                        className="w-4 h-4 md:w-5 md:h-5 text-emerald-400"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <path d="M7 7h10M7 12h10M7 17h10" />
                      </svg>
                    </div>
                    <span className="text-[9px] md:text-[10px] text-gray-400 mt-1 font-mono">
                      Transform
                    </span>
                  </div>

                  {/* IF/Router node */}
                  <div className="absolute left-[72%] top-[30%] flex flex-col items-center">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-violet-500/30 border border-violet-500/50 flex items-center justify-center shadow-lg shadow-violet-500/10 ring-2 ring-violet-400/30 animate-pulse">
                      <svg
                        className="w-4 h-4 md:w-5 md:h-5 text-violet-400"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5" />
                      </svg>
                    </div>
                    <span className="text-[9px] md:text-[10px] text-gray-400 mt-1 font-mono">
                      IF
                    </span>
                  </div>
                </div>

                {/* Running indicator */}
                <div className="absolute bottom-1 right-1 md:bottom-2 md:right-2 flex items-center gap-1 px-2 py-0.5 bg-emerald-500/20 border border-emerald-500/30 rounded-full">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-[9px] md:text-[10px] text-emerald-400 font-mono">
                    Running
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Monitor stand */}
        <div className="flex justify-center">
          <div
            className="w-[25%] h-4 md:h-5"
            style={{ background: 'linear-gradient(180deg, #1f2937 0%, #111827 100%)' }}
          />
        </div>
        <div className="flex justify-center -mt-0.5">
          <div
            className="w-[40%] h-2 md:h-2.5 rounded-b-lg"
            style={{
              background: 'linear-gradient(180deg, #374151 0%, #1f2937 100%)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            }}
          />
        </div>
      </div>

      {/* Feature badges - Right side */}
      <div className="hidden md:block absolute right-[6%] top-[10%] z-20">
        <div className="flex flex-col gap-2">
          <div className="group flex items-center gap-2 px-3 py-2 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-lg shadow-md border border-gray-200/80 dark:border-gray-700/80 hover:shadow-lg transition-all">
            <div
              className={cn(
                'w-7 h-7 rounded-md bg-gradient-to-br flex items-center justify-center shadow-sm',
                colors.gradient
              )}
            >
              <svg className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-gray-900 dark:text-white leading-tight">
                {t('integrations.title')}
              </span>
              <span className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
                Plug &amp; Play
              </span>
            </div>
          </div>

          <div className="group flex items-center gap-2 px-3 py-2 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-lg shadow-md border border-gray-200/80 dark:border-gray-700/80 hover:shadow-lg transition-all">
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-violet-400 to-purple-600 flex items-center justify-center shadow-sm">
              <svg
                className="w-3.5 h-3.5 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 2a8 8 0 0 0-8 8c0 3.4 2.1 6.3 5 7.5V20h6v-2.5c2.9-1.2 5-4.1 5-7.5a8 8 0 0 0-8-8z" />
                <path d="M9 22h6" />
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-gray-900 dark:text-white leading-tight">
                {t('aiNodes.title')}
              </span>
              <span className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
                LangChain
              </span>
            </div>
          </div>

          <div className="group flex items-center gap-2 px-3 py-2 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-lg shadow-md border border-gray-200/80 dark:border-gray-700/80 hover:shadow-lg transition-all">
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-emerald-400 to-green-600 flex items-center justify-center shadow-sm">
              <svg className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-gray-900 dark:text-white leading-tight">
                Self-Hosted
              </span>
              <span className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
                100% Privacy
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tech stack badges - Bottom */}
      <div className="absolute left-1/2 md:left-[42%] -translate-x-1/2 bottom-[2%] z-30">
        <div className="flex items-center justify-center gap-1.5 md:gap-3">
          {[
            { name: 'Docker', color: 'bg-[#2496ED]', label: 'D' },
            { name: 'PostgreSQL', color: 'bg-[#336791]', label: 'P' },
            { name: 'Caddy', color: 'bg-[#00A344]', label: 'C' },
          ].map((tech) => (
            <div key={tech.name} className="group relative">
              <div className="relative flex items-center gap-1 md:gap-1.5 h-7 md:h-9 px-2 md:px-3 bg-white dark:bg-gray-800 rounded md:rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm group-hover:shadow-md transition-all duration-300 group-">
                <div
                  className={cn(
                    'w-4 h-4 md:w-5 md:h-5 rounded flex items-center justify-center flex-shrink-0',
                    tech.color
                  )}
                >
                  <span className="text-white text-[10px] md:text-[11px] font-bold">
                    {tech.label}
                  </span>
                </div>
                <span className="hidden md:inline text-xs font-semibold text-gray-700 dark:text-gray-200">
                  {tech.name}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Terminal Visual (OpenClaw) ──────────────────────────────────────────────

function TerminalVisual({
  colors,
  appName,
  className,
}: { colors: (typeof accentColors)['purple']; appName: string; className?: string }) {
  const t = useTranslations('vpsApps.openclaw.features.items');

  return (
    <div className={cn('relative w-full aspect-[4/3] max-w-2xl mx-auto', className)}>
      {/* Ambient glow */}
      <div
        className="absolute z-0 right-[10%] bottom-[20%] w-[45%] h-[50%] rounded-full blur-2xl"
        style={{
          background: `radial-gradient(circle, ${colors.glow} 0%, ${colors.glowLight} 45%, transparent 70%)`,
        }}
      />

      {/* Monitor frame */}
      <div
        className="absolute left-[5%] md:left-[8%] bottom-[12%] w-[72%] md:w-[68%] z-10"
        style={{
          transform: 'perspective(1200px) rotateY(6deg) rotateX(1deg)',
          transformOrigin: 'left center',
        }}
      >
        <div
          className="relative rounded-xl md:rounded-xl overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, #1f2937 0%, #111827 100%)',
            boxShadow: '0 25px 50px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.05) inset',
            padding: '8px',
          }}
        >
          <div
            className="relative rounded-lg md:rounded-xl overflow-hidden"
            style={{ background: '#0f172a', padding: '4px' }}
          >
            <div className="relative rounded-md md:rounded-lg overflow-hidden aspect-video bg-[#0c0c1d]">
              {/* Terminal header */}
              <div className="flex items-center gap-1.5 px-2 md:px-3 py-1.5 md:py-2 bg-gray-900 border-b border-gray-700">
                <div className="flex gap-1">
                  <div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-red-500" />
                  <div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-yellow-500" />
                  <div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-green-500" />
                </div>
                <div className="flex-1 ml-2 md:ml-3">
                  <span className="text-[10px] md:text-[11px] text-gray-400 font-mono">
                    root@vps-nl-01 — openclaw onboard
                  </span>
                </div>
              </div>

              {/* Terminal content */}
              <div className="p-2 md:p-3 font-mono text-[9px] md:text-[10px] leading-relaxed space-y-0.5">
                <div>
                  <span className="text-violet-400">root@vps-nl-01:~#</span>{' '}
                  <span className="text-gray-300">openclaw onboard</span>
                </div>
                <div className="text-gray-500">Starting OpenClaw onboarding...</div>
                <div className="h-1" />
                <div className="text-violet-300">{'  '}___ ____ _</div>
                <div className="text-violet-300"> / _ \\ _ __ ___ _ __ / ___| | __ ___ __</div>
                <div className="text-violet-300">
                  | | | | &apos;_ \\ / _ \\ &apos;_ | | | |/ _` \\ \\ /\\ / /
                </div>
                <div className="text-violet-300">| |_| | |_) | __/ | | | |___| | (_| |\\ V V /</div>
                <div className="text-violet-300">
                  {' '}
                  \\___/| .__/ \\___|_| |_|\\____|_|\\__,_| \\_/\\_/
                </div>
                <div className="h-1" />
                <div>
                  <span className="text-emerald-400">✓</span>{' '}
                  <span className="text-gray-400">Node.js 22.x detected</span>
                </div>
                <div>
                  <span className="text-emerald-400">✓</span>{' '}
                  <span className="text-gray-400">Go 1.23 detected</span>
                </div>
                <div>
                  <span className="text-emerald-400">✓</span>{' '}
                  <span className="text-gray-400">Memory: 4096 MB (recommended)</span>
                </div>
                <div className="h-0.5" />
                <div>
                  <span className="text-violet-400">?</span>{' '}
                  <span className="text-white">Select AI provider</span>{' '}
                  <span className="text-gray-500">&gt;</span>{' '}
                  <span className="text-violet-300">Claude (Anthropic)</span>
                </div>
                <div className="flex items-center gap-0.5">
                  <span className="text-violet-400">root@vps-nl-01:~#</span>
                  <span className="w-1 h-2 bg-violet-400 animate-pulse" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Monitor stand */}
        <div className="flex justify-center">
          <div
            className="w-[25%] h-4 md:h-5"
            style={{ background: 'linear-gradient(180deg, #1f2937 0%, #111827 100%)' }}
          />
        </div>
        <div className="flex justify-center -mt-0.5">
          <div
            className="w-[40%] h-2 md:h-2.5 rounded-b-lg"
            style={{
              background: 'linear-gradient(180deg, #374151 0%, #1f2937 100%)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            }}
          />
        </div>
      </div>

      {/* Feature badges - Right side */}
      <div className="hidden md:block absolute right-[6%] top-[10%] z-20">
        <div className="flex flex-col gap-2">
          <div className="group flex items-center gap-2 px-3 py-2 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-lg shadow-md border border-gray-200/80 dark:border-gray-700/80 hover:shadow-lg transition-all">
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-violet-400 to-purple-600 flex items-center justify-center shadow-sm">
              <svg
                className="w-3.5 h-3.5 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-gray-900 dark:text-white leading-tight">
                13+ Platforms
              </span>
              <span className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
                WhatsApp, Telegram...
              </span>
            </div>
          </div>

          <div className="group flex items-center gap-2 px-3 py-2 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-lg shadow-md border border-gray-200/80 dark:border-gray-700/80 hover:shadow-lg transition-all">
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-emerald-400 to-green-600 flex items-center justify-center shadow-sm">
              <svg
                className="w-3.5 h-3.5 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="2" y="3" width="20" height="14" rx="2" />
                <path d="M8 21h8M12 17v4" />
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-gray-900 dark:text-white leading-tight">
                {t('webBrowsing.title')}
              </span>
              <span className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
                Autonomous
              </span>
            </div>
          </div>

          <div className="group flex items-center gap-2 px-3 py-2 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-lg shadow-md border border-gray-200/80 dark:border-gray-700/80 hover:shadow-lg transition-all">
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center shadow-sm">
              <svg className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-gray-900 dark:text-white leading-tight">
                {t('privacyFirst.title')}
              </span>
              <span className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
                Your Server
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tech stack badges - Bottom */}
      <div className="absolute left-1/2 md:left-[42%] -translate-x-1/2 bottom-[2%] z-30">
        <div className="flex items-center justify-center gap-1.5 md:gap-3">
          {[
            { name: 'Node.js', color: 'bg-[#339933]', label: 'N' },
            { name: 'Go', color: 'bg-[#00ADD8]', label: 'Go' },
            { name: 'UFW', color: 'bg-[#E95420]', label: 'U' },
          ].map((tech) => (
            <div key={tech.name} className="group relative">
              <div className="relative flex items-center gap-1 md:gap-1.5 h-7 md:h-9 px-2 md:px-3 bg-white dark:bg-gray-800 rounded md:rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm group-hover:shadow-md transition-all duration-300 group-">
                <div
                  className={cn(
                    'w-4 h-4 md:w-5 md:h-5 rounded flex items-center justify-center flex-shrink-0',
                    tech.color
                  )}
                >
                  <span className="text-white text-[10px] md:text-[11px] font-bold">
                    {tech.label}
                  </span>
                </div>
                <span className="hidden md:inline text-xs font-semibold text-gray-700 dark:text-gray-200">
                  {tech.name}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
